// screen: Live — Channels list (live spec "Channels list")
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Radio } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { EmptyState, ErrorState, Skeleton } from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { ChannelStateBadge, FeedBadges, hms, useChannels, useNow, useOnAirProgramme, type Channel } from '~/app/live';
import { hasLiveStill, LiveTilePreview } from '~/app/LiveTilePreview';
import { LIVE_STILL_REFRESH_S } from '~/app/thumbs';
import { useFastChannels } from '~/app/fast';
import { usePlan } from '~/app/selfserve';
import { AddInternalChannel, InternalBadge } from '~/app/InternalChannel';

export const Route = createFileRoute('/_layout/t/$slug/live')({ component: Screen });

function Screen() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  React.useEffect(() => { document.title = appTitle(t`Live`); }, [t]);
  const q = useChannels(tenant.slug);
  const fast = useFastChannels(tenant.slug); // v3 §11: the link shows only where FAST channels are switched on
  const plan = usePlan(tenant.slug); // v3 §17: Free / Starter have no live channels
  const now = useNow(LIVE_STILL_REFRESH_S * 1000); // tiles move to a newer recorder still every 30 s

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">{t`Live`}</h1>
        <div className="flex flex-wrap items-center gap-3">
          {fast.data?.available ? (
            <Link to={'/t/$slug/fast' as never} params={{ slug: tenant.slug } as never} className="text-sm text-primary underline-offset-4 hover:underline">{t`FAST channels`}</Link>
          ) : null}
          {q.data && q.data.length > 0 ? <AddInternalChannel /> : null}
        </div>
      </div>
      {q.isPending ? <Skeleton className="h-48 w-full" /> : q.isError ? (
        <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load channels`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
      ) : q.data.length === 0 ? (
        <EmptyState icon={<Radio className="size-8" aria-hidden />} title={t`No channels yet`}
          description={plan.data && plan.data.tier !== 'custom' ? t`Live channels are not included in your plan. Write to sales@viewstream.co.il for a plan with live channels.` : t`Channels are set up by ViewStream. Ask your account manager to add one.`} />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-label={t`Channels`}>
          {q.data.map((c) => <ChannelCard key={c.id} c={c} now={now} />)}
        </ul>
      )}
    </div>
  );
}

function ChannelCard({ c, now }: { c: Channel; now: number }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const onAir = useOnAirProgramme(tenant.slug, c.id, now, hasLiveStill(c.state));
  const name = c.title ?? c.slug ?? '';
  return (
    <li className="rounded-lg border border-border bg-surface shadow-card" data-channel={c.slug}>
      <Link to={'/t/$slug/live/$channelId' as never} params={{ slug: tenant.slug, channelId: c.id } as never} className="block rounded-lg focus-visible:outline-2 focus-visible:outline-primary">
        <LiveTilePreview channel={c} cdn={tenant.cdn_hostname} tenant={tenant.slug} now={now} className="rounded-t-lg" />
        <div className="space-y-2 p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate font-medium">{name}</span>
            <ChannelStateBadge state={c.state} />
          </div>
          {onAir?.title ? (
            <p className="truncate text-sm" data-testid="on-air" title={onAir.title}>
              <span className="text-fg-muted">{t`Now:`}</span> <bdi>{onAir.title}</bdi>
            </p>
          ) : null}
          <InternalBadge ch={c} className="text-xs" />
          <FeedBadges ch={c} className="text-xs" />
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-fg-muted">
            <dt>{t`Ladder`}</dt><dd className="ltr-isolate">{c.ladder}</dd>
            <dt>{t`DVR window`}</dt><dd className="num">{hms((c.dvr_window_s ?? 0) * 1000)}</dd>
            <dt>{t`Recording`}</dt><dd>{t`${c.retention_days ?? 0} days`}</dd>
            <dt>{t`Viewers`}</dt><dd title={t`Audience statistics arrive with milestone M6`}>—</dd>
          </dl>
        </div>
      </Link>
    </li>
  );
}
